import React, { useEffect, useRef, useState } from 'react';
import { Check, Rocket, KeyRound, AlertCircle, History } from 'lucide-react';
import { api } from '../../services/apiService';

import { RubyGemMark } from '../RubyGemMark';
/**
 * SET UP POS — the last action in Button layout.
 *
 * Enabling buttons only decides WHAT a till may show; this puts the RubyTill
 * default on it (action bar, keypad, employee discount, white customer screen,
 * light theme) so the first cashier signs in to a working screen.
 *
 * THE RESULT STAYS. The last run's steps, its time and how many times it has
 * been run are stored on the register, so the finished list is still here
 * after the order screen reloads — a record of what was installed and when.
 * The button stays too: running it again replaces the record and adds one to
 * the count.
 */

interface Step { id: string; label: string; detail: string }

export interface LastSetup { at: number; count: number; steps: Step[] }

interface Props {
  tenantId: string;
  registerId: string;
  registerName: string;
  /** Buttons enabled and saved on the register. */
  enabledCount: number;
  /** Unsaved toggles — set up must run on what is saved. */
  dirty: boolean;
  lastSetup?: LastSetup | null;
  onDone?: () => void;
}

const STEP_MS = 380;

const stamp = (ts: number) =>
  new Date(ts).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });

const SetupPosCard: React.FC<Props> = ({ tenantId, registerId, registerName, enabledCount, dirty, lastSetup, onDone }) => {
  const [phase, setPhase] = useState<'idle' | 'running' | 'revealing' | 'error'>('idle');
  const [run, setRun] = useState<LastSetup | null>(null);
  const [shown, setShown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => () => { if (timer.current) window.clearInterval(timer.current); }, []);

  // A fresh run here wins over the stored record until the register reloads with it.
  const record = run || lastSetup || null;

  const blocked = dirty
    ? 'Save the enabled buttons first.'
    : enabledCount === 0
      ? 'Enable the buttons for this register first.'
      : null;

  const start = async () => {
    if (blocked) return;
    setPhase('running');
    setError(null);
    setShown(0);
    try {
      const res: any = await api.posConfig.setupRegister(registerId, tenantId);
      const next: LastSetup = { at: res?.posSetupAt || Date.now(), count: res?.posSetupCount || 1, steps: res?.steps || [] };
      setRun(next);
      setPhase('revealing');
      let n = 0;
      timer.current = window.setInterval(() => {
        n += 1;
        setShown(n);
        if (n >= next.steps.length) {
          if (timer.current) window.clearInterval(timer.current);
          timer.current = null;
          window.setTimeout(() => { setPhase('idle'); onDone?.(); }, STEP_MS);
        }
      }, STEP_MS);
    } catch (e: any) {
      setError(e?.message || 'Could not set up this register.');
      setPhase('error');
    }
  };

  const busy = phase === 'running' || phase === 'revealing';
  const visibleSteps = record?.steps || [];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-stone-800 font-semibold text-sm">Set up POS</h3>
          <p className="text-stone-500 text-xs mt-0.5">
            Installs the RubyTill default on <span className="font-medium text-stone-600">{registerName}</span> — action bar,
            keypad, employee discount and a white customer screen — using only the enabled buttons.
          </p>
        </div>
        <button
          type="button"
          onClick={start}
          disabled={!!blocked || busy}
          className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap px-4 min-h-[44px] text-sm font-semibold rounded-lg bg-[#DC143C] text-white hover:bg-[#9F1239] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {busy ? <RubyGemMark size={15} drawing color="currentColor" /> : <Rocket size={15} />}
          {busy ? 'Setting up…' : record ? 'Set up again' : 'Set up POS'}
        </button>
      </div>

      {blocked && !busy && (
        <p className="mt-3 text-[12px] text-stone-400">{blocked}</p>
      )}

      {phase === 'error' && (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {phase === 'running' && (
        <div className="mt-4 flex items-center gap-3 rounded-xl bg-stone-50 px-3 py-2.5">
          <RubyGemMark size={16} drawing />
          <span className="text-sm text-stone-600">Installing the layout…</span>
        </div>
      )}

      {record && phase !== 'running' && (
        <>
          <ol className="mt-4 space-y-1.5">
            {visibleSteps.map((s, i) => {
              const revealing = phase === 'revealing';
              const done = !revealing || i < shown;
              const current = revealing && i === shown;
              if (!done && !current) return null;
              return (
                <li key={s.id} className="flex items-center gap-3 rounded-xl bg-stone-50 px-3 py-2.5" style={revealing ? { animation: 'setupFadeIn .25s ease-out' } : undefined}>
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors ${done ? 'bg-emerald-500' : 'bg-[#FFF1F3]'}`}>
                    {done ? <Check size={14} className="text-white" strokeWidth={3} /> : <RubyGemMark size={13} drawing />}
                  </span>
                  <span className="flex-1 text-sm text-stone-700">{s.label}</span>
                  <span className="text-xs font-medium text-stone-500">{s.detail}</span>
                </li>
              );
            })}
          </ol>

          {phase !== 'revealing' && (
            <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" style={run ? { animation: 'setupFadeIn .3s ease-out' } : undefined}>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500">
                <KeyRound size={17} className="text-white" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-emerald-800">Ready to use</p>
                <p className="text-xs text-emerald-700">Staff can sign in on {registerName} with their PIN.</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] font-semibold text-emerald-800">Set up {stamp(record.at)}</p>
                <p className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                  <History size={11} /> {record.count === 1 ? '1 attempt' : `${record.count} attempts`}
                </p>
              </div>
            </div>
          )}
        </>
      )}

      <style>{'@keyframes setupFadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}'}</style>
    </div>
  );
};

export default SetupPosCard;
